import UiBox from '../Box';
import UiText from '../Typography/UiText';
import React from 'react';
import { Grid, styled } from '@mui/material';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { JSSLink, LinkType } from '../Links/JSSLink.styles';
import UiHeading from '../Heading';

type ChildSlot = {
  title?: React.ReactElement | null;
  description?: React.ReactElement | null;
  label?: React.ReactElement | null;
  metaText?: React.ReactElement | null;
  image?: React.ReactElement | null;
  button?: React.ReactElement | null;
};

type Props = {
  borderRadius?: string;
  background?: string;
  children: ChildSlot;
  disableBorder?: boolean;
  disableShadow?: boolean;
  removeImagePadding?: boolean;
  height?: React.CSSProperties['height'];
};

type ImageProps = {
  height?: string;
  borderRadius?: string;
  position?: ImagePositionType;
  children?: React.ReactElement;
};

type LabelProps = Pick<React.ComponentProps<typeof UiText>, 'size'> & {
  children: React.ReactElement;
  align?: GridAlignType;
};

type HeadingProps = {
  children: React.ReactElement;
};

type DescriptionProps = {
  children: React.ReactElement;
};

enum GridAlignment {
  Left = 'flex-start',
  Center = 'center',
  Right = 'flex-end',
}

export enum ImagePosition {
  Top = 'top',
  Center = 'center',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
  TopLeft = 'top left',
  TopRight = 'top right',
  BottomLeft = 'bottom left',
  BottomRight = 'bottom right',
}

export type ImagePositionType = keyof typeof ImagePosition;
export type GridAlignType = keyof typeof GridAlignment;
export type ButtonVariantType = keyof typeof JssLinkButtonVariantMap;

type ButtonProps = {
  children: LinkField;
  variant?: ButtonVariantType;
  align?: GridAlignType;
  fullWidth?: boolean;
};

const JssLinkButtonVariantMap: { [key in string]: LinkType } = {
  contained: 'contained',
  outlined: 'outlined',
  text: 'text',
};

const StyledCardWithHoverState = styled(UiBox)(() => ({
  display: 'grid',
  gridTemplateRows: 'auto 1fr auto',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.01)',
  },
}));

const CARD_PADDING = '16px';

function Card({
  background,
  borderRadius,
  disableBorder,
  disableShadow,
  height,
  removeImagePadding,
  children,
}: Props) {
  return (
    <StyledCardWithHoverState
      styles={{
        height,
        background,
        borderRadius,
        padding: CARD_PADDING,
        border: disableBorder ? 'unset' : '1px solid #E9E9E9',
        boxShadow: disableShadow ? 'none' : '0 2px 4px #0000000f',
        overflow: 'hidden',
      }}
      id="ui-card"
      className="ui-card-wrapper"
      data-testid="card"
    >
      <UiBox
        className="ui-card-image"
        styles={{
          marginTop: removeImagePadding ? '-' + CARD_PADDING : 0,
          marginLeft: removeImagePadding ? '-' + CARD_PADDING : 0,
          marginRight: removeImagePadding ? '-' + CARD_PADDING : 0,
        }}
      >
        {children?.image}
      </UiBox>
      <UiBox className="ui-card-body">
        <Grid container spacing={'6px'}>
          {children?.label && (
            <Grid item xs={12} className="ui-card-label">
              {children?.label}
            </Grid>
          )}
          {(children?.title || children.description) && (
            <Grid item xs={12} className="ui-card-heading">
              {children?.title}
              {children?.description}
            </Grid>
          )}
          {children?.metaText && (
            <Grid item xs={12} className="ui-card-meta-text">
              {children?.metaText}
            </Grid>
          )}
        </Grid>
      </UiBox>
      {children?.button && (
        <UiBox className="ui-card-footer" styles={{ paddingTop: '8px' }}>
          {children?.button}
        </UiBox>
      )}
    </StyledCardWithHoverState>
  );
}

const StyledImageWrapper = styled('div')(
  ({
    borderRadius,
    height,
    position,
  }: {
    borderRadius?: string;
    height?: string;
    position?: string;
  }) => ({
    img: {
      width: '100%',
      height: height || 'auto',
      objectFit: 'cover',
      borderRadius: borderRadius,
      objectPosition: position,
    },
  })
);

function CardImage({ borderRadius, children, height, position = 'Center' }: ImageProps) {
  return (
    <StyledImageWrapper
      borderRadius={borderRadius}
      height={height}
      position={ImagePosition[position]}
    >
      {children}
    </StyledImageWrapper>
  );
}

function CardLabel({ align = 'Left', children, ...rest }: LabelProps) {
  return (
    <Grid container justifyContent={GridAlignment[align]}>
      <Grid item xs="auto">
        <UiText color="default" {...rest}>
          {children}
        </UiText>
      </Grid>
    </Grid>
  );
}

function CardTitle(props: HeadingProps) {
  return (
    <UiHeading size="h4" isChildren>
      {props?.children}
    </UiHeading>
  );
}

function CardDescription(props: DescriptionProps) {
  return <UiText color="default">{props?.children}</UiText>;
}

function CardButton({ variant = 'outlined', align = 'Center', fullWidth, children }: ButtonProps) {
  return (
    <Grid container justifyContent={GridAlignment[align]}>
      <Grid item xs={fullWidth ? 12 : 'auto'}>
        <JSSLink
          className={'image-card-btn btn'}
          data-testid="cta"
          field={children}
          variant={JssLinkButtonVariantMap[variant]}
          style={{ width: '100%' }}
        />
      </Grid>
    </Grid>
  );
}

Card.Image = CardImage;
Card.Label = CardLabel;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Button = CardButton;

export default Card;
